import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowRight, Target, Brain, Users, CheckCircle, ChevronRight, Zap, Send, Check, DollarSign } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const phases = [
  { num: 1, title: "Foundation", weeks: "Weeks 1-6", desc: "Self-awareness, personal presence, and emotional intelligence baseline" },
  { num: 2, title: "Communication & Influence", weeks: "Weeks 7-12", desc: "Executive communication, stakeholder navigation, board-level presence" },
  { num: 3, title: "Resilience & Crisis", weeks: "Weeks 13-18", desc: "Crisis leadership, cognitive resilience, organizational recovery" },
  { num: 4, title: "Strategic Authority", weeks: "Weeks 19-24", desc: "Vision, legacy, innovation, and mentoring the next generation" },
];

const features = [
  { icon: Target, title: "Executive Presence", desc: "Command any room with authentic authority and gravitas" },
  { icon: Brain, title: "AI-Powered Coaching", desc: "24/7 access to an elite AI coach trained in risk leadership" },
  { icon: Users, title: "Cohort-Based Learning", desc: "Learn alongside peers who understand your unique challenges" },
];

function DemoForm({ variant = 'hero' }) {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const response = await fetch(`${API}/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', company: '', phone: '', message: '' });
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
    setIsSubmitting(false);
  };

  if (submitted) {
    return (
      <div data-testid="demo-form-success" className="bg-[#141414] border border-[#22C55E]/30 rounded-sm p-8 text-center">
        <div className="w-12 h-12 bg-[#22C55E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-6 h-6 text-[#22C55E]" />
        </div>
        <h3 className="text-white font-bold text-xl mb-2">Demo Request Received</h3>
        <p className="text-[#A3A3A3] text-sm">Thanks! We'll be in touch soon.</p>
      </div>
    );
  }

  const inputClass = "w-full bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-2.5 text-white text-sm placeholder:text-[#525252] focus:outline-none focus:ring-1 focus:ring-[#FF3B30] focus:border-[#FF3B30] transition-colors";

  return (
    <form data-testid="demo-request-form" onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input data-testid="demo-name-input" type="text" placeholder="Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className={inputClass} />
        <input data-testid="demo-email-input" type="email" placeholder="Work Email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className={inputClass} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input data-testid="demo-company-input" type="text" placeholder="Company" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} className={inputClass} />
        <input data-testid="demo-phone-input" type="tel" placeholder="Phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className={inputClass} />
      </div>
      <textarea data-testid="demo-message-input" placeholder="Tell us about your training goals" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} rows={3} className={`${inputClass} resize-none`} />
      {error && <p data-testid="demo-form-error" className="text-[#FF3B30] text-xs">{error}</p>}
      <button
        data-testid="demo-submit-button"
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-[#FF3B30] hover:bg-[#D62828] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3 rounded-sm transition-all duration-200 flex items-center justify-center gap-2 hover:-translate-y-0.5"
      >
        {isSubmitting ? 'Submitting...' : 'Request Demo'} {!isSubmitting && <Send className="w-4 h-4" />}
      </button>
    </form>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-[#FF3B30]" />
            <span className="font-heading text-white font-bold text-xl tracking-tight">RiskCoach<span className="text-[#FF3B30]">.ai</span></span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login" data-testid="nav-login-button" className="text-[#A3A3A3] hover:text-white text-sm font-medium transition-colors">
              Sign In
            </Link>
            <a
              href="#request-demo"
              data-testid="nav-demo-button"
              className="bg-[#FF3B30] hover:bg-[#D62828] text-white text-sm font-bold px-5 py-2 rounded-sm transition-colors"
            >
              Request Demo
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-16 min-h-screen flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1633602114554-be8c2c5d6828?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc0NjYwMjUxfDA&ixlib=rb-4.1.0&q=85)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]/60" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm px-3 py-1 mb-6">
              <Zap className="w-3.5 h-3.5 text-[#FF3B30]" />
              <span className="text-[#FF3B30] text-xs font-bold tracking-[0.2em] uppercase">24-Week Cohort Program</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black leading-[1.05] mb-6">
              Forge Executive<br />
              Presence.<br />
              <span className="text-[#FF3B30]">Build Resilience.</span>
            </h1>
            <p className="text-[#A3A3A3] text-lg leading-relaxed max-w-xl mb-8">
              The definitive coaching program for risk managers who want to command the boardroom,
              lead through crisis, and build a legacy of strategic authority.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="#request-demo"
                data-testid="hero-cta-button"
                className="bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold px-8 py-3.5 rounded-sm transition-all duration-200 flex items-center gap-2 hover:-translate-y-0.5"
              >
                Request a Demo <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/login"
                data-testid="hero-signin-button"
                className="border border-[#262626] hover:border-white text-white px-6 py-3.5 rounded-sm transition-all duration-200 text-sm font-medium"
              >
                Sign In
              </Link>
            </div>
          </div>
          <div id="request-demo" className="lg:col-span-5">
            <div className="bg-[#141414] border border-[#262626] rounded-sm p-6 space-y-5">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF3B30] mb-1">Get Started</p>
                <p className="text-white font-semibold text-lg">Request a Demo</p>
                <p className="text-[#737373] text-xs mt-1">See how RiskCoach.ai transforms risk leaders.</p>
              </div>
              <DemoForm variant="hero" />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 border-t border-[#262626]">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF3B30] mb-4">What You Get</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-12">
            Built for Risk Leaders Who<br />Refuse to Be Average
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-[#141414] border border-[#262626] rounded-sm p-6 hover:-translate-y-1 hover:border-[#FF3B30]/50 transition-all duration-300 group">
                <div className="w-10 h-10 bg-[#FF3B30]/10 rounded-sm flex items-center justify-center mb-4 group-hover:bg-[#FF3B30]/20 transition-colors">
                  <Icon className="w-5 h-5 text-[#FF3B30]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 Phases */}
      <section className="py-24 border-t border-[#262626]">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF3B30] mb-4">The Journey</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-12">
            24 Weeks. 4 Phases.<br />Complete Transformation.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {phases.map((p) => (
              <div key={p.num} className="bg-[#141414] border border-[#262626] rounded-sm p-6 flex gap-5 hover:border-[#FF3B30]/50 transition-all duration-300">
                <div className="w-12 h-12 bg-[#FF3B30] rounded-sm flex items-center justify-center shrink-0">
                  <span className="text-white font-bold text-lg">{p.num}</span>
                </div>
                <div>
                  <p className="text-[#737373] text-xs font-bold tracking-wider uppercase">{p.weeks}</p>
                  <h3 className="text-white font-semibold text-lg mt-1">{p.title}</h3>
                  <p className="text-[#A3A3A3] text-sm mt-2 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 border-t border-[#262626]">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF3B30] mb-4">Investment</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-4">
            Choose Your Path
          </h2>
          <p className="text-[#A3A3A3] text-lg mb-12 max-w-2xl">Two ways to transform your executive presence and build resilience that lasts.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
            {/* Individual */}
            <div data-testid="pricing-individual" className="bg-[#141414] border border-[#FF3B30]/40 rounded-sm p-8 relative">
              <div className="absolute -top-3 left-6 bg-[#FF3B30] text-white text-xs font-bold px-3 py-1 tracking-wider uppercase">Most Popular</div>
              <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.15em] uppercase mb-2">Individual</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-white font-heading text-4xl font-black">$2,497</span>
                <span className="text-[#737373] text-sm">one-time</span>
              </div>
              <p className="text-[#525252] text-xs mb-6">or $497/mo x 6 months</p>
              <ul className="space-y-3 mb-8">
                {["Full 24-week curriculum", "Unlimited AI coaching sessions", "Executive Presence Assessment", "12 & 24-week reassessments", "Personal progress tracking", "Reflections & growth journal"].map(item => (
                  <li key={item} className="flex items-start gap-2.5 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#22C55E] shrink-0 mt-0.5" />
                    <span className="text-[#A3A3A3]">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                data-testid="pricing-individual-cta"
                className="block text-center bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold py-3 rounded-sm transition-all duration-200 hover:-translate-y-0.5"
              >
                Get Started
              </Link>
            </div>
            {/* Enterprise */}
            <div data-testid="pricing-enterprise" className="bg-[#141414] border border-[#262626] rounded-sm p-8">
              <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.15em] uppercase mb-2">Enterprise / Teams</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-white font-heading text-4xl font-black">Custom</span>
              </div>
              <p className="text-[#525252] text-xs mb-6">Volume pricing for 5+ seats</p>
              <ul className="space-y-3 mb-8">
                {["Everything in Individual", "Team progress dashboard", "Dedicated account manager", "Custom onboarding", "Cohort scheduling", "Priority support"].map(item => (
                  <li key={item} className="flex items-start gap-2.5 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#22C55E] shrink-0 mt-0.5" />
                    <span className="text-[#A3A3A3]">{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#request-demo"
                data-testid="pricing-enterprise-cta"
                className="block text-center border border-[#262626] hover:border-white text-white font-bold py-3 rounded-sm transition-all duration-200"
              >
                Request Demo
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-[#262626]">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-6">
            Ready to Lead with<br /><span className="text-[#FF3B30]">Unshakeable Authority?</span>
          </h2>
          <p className="text-[#A3A3A3] text-lg mb-8 max-w-xl mx-auto">
            Request a demo and see how RiskCoach.ai transforms risk leaders in 24 weeks.
          </p>
          <div className="bg-[#141414] border border-[#262626] rounded-sm p-6 text-left">
            <DemoForm variant="bottom" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#262626] py-8">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#FF3B30]" />
            <span className="text-[#737373] text-sm">RiskCoach.ai</span>
          </div>
          <p className="text-[#737373] text-xs">Executive Presence & Resilience for Risk Leaders</p>
        </div>
      </footer>
    </div>
  );
}
